/**
 * cart-normalizer-theme-only.js
 *
 * Classifies each cart variant using quantity math + pickup availability:
 *
 *   totalQty      = ProductVariant.quantityAvailable (all sellable stock, all locations)
 *   pickupQty     = sum of storeAvailability[].quantityAvailable (retail locations)
 *   warehouseQty  = totalQty - pickupQty
 *   pickupAvail   = any storeAvailability node has available === true
 *
 *   warehouseQty > 0                -> SHIPPABLE (has courier/warehouse stock)
 *   warehouseQty > 0 && !pickupAvail -> SHIPPING-ONLY (shippable, but no pickup at any store)
 *   warehouseQty <= 0               -> PICKUP-ONLY (all stock is at retail locations)
 *
 * The modal is shown only when SHIPPING-ONLY items coexist with PICKUP-ONLY items,
 * because there is no single fulfillment method that covers both.
 * A cart mixing SHIPPABLE+PICKUP items with PICKUP-ONLY items is fine — everything
 * can be picked up, so checkout proceeds normally.
 */
(function () {
  'use strict';

  var TAG = '[CartNormalizer]';

  // Feature flags — set to false to disable without removing code
  var CHECKOUT_INTERCEPT_ENABLED = true; // mixed-cart warning at checkout

  // Config

  var configEl = document.getElementById('cart-normalizer-config');
  if (!configEl) return;

  var config;
  try {
    config = JSON.parse(configEl.textContent.trim());
  } catch (e) {
    console.error(e);
    return;
  }

  var shop  = config.shop;
  var token = config.storefrontToken;

  if (!shop || !token) {
    return;
  }

  var pendingPickupItems = null;

  // Storefront API query
  //
  // quantityAvailable on the variant = total sellable stock across ALL locations.
  // quantityAvailable on each storeAvailability node = stock at that pickup location.
  // Difference = stock at warehouse/courier locations (no pickup enabled there).

  function buildQuery() {
    return (
      'query($ids:[ID!]!) {' +
        'nodes(ids:$ids) {' +
          '... on ProductVariant {' +
            'id ' +
            'availableForSale ' +
            'quantityAvailable ' +
            'product { title } ' +
            'storeAvailability(first:100) {' +
              'nodes { available quantityAvailable location { id name } }' +
            '}' +
          '}' +
        '}' +
      '}'
    );
  }

  function fetchVariantData(variantGids) {
    return fetch('https://' + shop + '/api/2024-01/graphql.json', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': token,
      },
      body: JSON.stringify({ query: buildQuery(), variables: { ids: variantGids } }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error('Storefront API HTTP ' + res.status);
        return res.json();
      })
      .then(function (json) {
        if (json.errors) throw new Error('Storefront API errors: ' + JSON.stringify(json.errors));
        var result = {};
        (json.data.nodes || []).forEach(function (node) {
          if (node && node.id) result[node.id] = node;
        });
        return result;
      });
  }

  // Classification
  function isShippable(variantData) {
    if (!variantData) return false;

    var title       = (variantData.product && variantData.product.title) || variantData.id;
    var pickupNodes = (variantData.storeAvailability && variantData.storeAvailability.nodes) || [];
    var pickupQty   = pickupNodes.reduce(function (sum, n) { return sum + (n.quantityAvailable || 0); }, 0);
    var totalQty    = variantData.quantityAvailable; // null when scope not granted

    if (typeof totalQty === 'number') {
      // Primary path: quantity math
      var warehouseQty = totalQty - pickupQty;
      var shippable    = warehouseQty > 0;
      
      return shippable;
    }

    var fallbackShippable = variantData.availableForSale === true && pickupNodes.length === 0;

    return fallbackShippable;
  }

  // Returns true if at least one store location has the item available for pickup,
  // regardless of reported quantity (handles warehouse-managed / pre-order stock).
  function hasPickupAvailability(variantData) {
    if (!variantData) return false;
    var pickupNodes = (variantData.storeAvailability && variantData.storeAvailability.nodes) || [];
    return pickupNodes.some(function (n) { return n.available === true; });
  }

  // Cart helpers
  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) {
      if (!r.ok) throw new Error('/cart.js ' + r.status);
      return r.json();
    });
  }

  function applyCartUpdates(updates) {
    return fetch('/cart/update.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ updates: updates }),
    }).then(function (r) {
      if (!r.ok) throw new Error('/cart/update.js ' + r.status);
      return r.json();
    });
  }

  // Cart classification
  function classifyCart() {
    return getCart().then(function (cart) {
      if (!cart.items || cart.items.length === 0) return null;

      var seen = new Set();
      var gids = [];
      cart.items.forEach(function (item) {
        var gid = 'gid://shopify/ProductVariant/' + item.variant_id;
        if (!seen.has(gid)) { seen.add(gid); gids.push(gid); }
      });

      return fetchVariantData(gids).then(function (variantMap) {
        var hasShippingOnly = false;
        var hasPickupOnly   = false;
        var pickupItems     = [];

        cart.items.forEach(function (item) {
          var gid = 'gid://shopify/ProductVariant/' + item.variant_id;
          var variantData = variantMap[gid];

          var shippable = isShippable(variantData);
          var pickable  = hasPickupAvailability(variantData);

          if (shippable && !pickable) {
            hasShippingOnly = true;
          }

          if (!shippable) {
            hasPickupOnly = true;
            pickupItems.push(item);
          }
        });

        if (!(hasShippingOnly && hasPickupOnly)) {
          return null;
        }

        return pickupItems;
      });
    });
  }

  // Notice helpers
  function escHtml(str) {
    return String(str).replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  function getNoticeEl() { return document.getElementById('cart-normalizer-notice'); }

  function showWarning(pickupItems) {
    var el = getNoticeEl();
    if (!el) return;

    var kachingFooter = document.querySelector('.kaching-cart__footer');
    if (kachingFooter && !kachingFooter.contains(el)) {
      kachingFooter.insertBefore(el, kachingFooter.firstChild);
    }

    var seenTitles = new Set();
    var titles     = [];
    pickupItems.forEach(function (item) {
      if (!seenTitles.has(item.product_title)) {
        seenTitles.add(item.product_title);
        titles.push(item.product_title);
      }
    });

    var listEl = el.querySelector('[data-items-list]');
    if (listEl) {
      listEl.innerHTML = titles.map(function (t) { return '<li>' + escHtml(t) + '</li>'; }).join('');
    }

    var confirmBtn = el.querySelector('[data-cart-normalizer-confirm]');
    if (confirmBtn) {
      confirmBtn.disabled    = false;
      confirmBtn.textContent = confirmBtn.dataset.labelDefault || confirmBtn.textContent;
    }

    el.hidden = false;
    setTimeout(function () {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, 50);
  }

  function hideNotice() {
    var el = getNoticeEl();
    if (el) el.hidden = true;
  }

  // Confirm: remove pickup-only items -> /checkout

  function handleConfirm() {
    if (!pendingPickupItems) return;

    var el         = getNoticeEl();
    var confirmBtn = el && el.querySelector('[data-cart-normalizer-confirm]');
    var cancelBtn  = el && el.querySelector('[data-cart-normalizer-cancel]');

    if (confirmBtn) { confirmBtn.disabled = true; confirmBtn.textContent = 'Kraunama\u2026'; }
    if (cancelBtn)  { cancelBtn.disabled  = true; }

    var updates        = {};
    var itemsToProcess = pendingPickupItems;
    pendingPickupItems = null;

    itemsToProcess.forEach(function (item) { updates[item.key] = 0; });

    applyCartUpdates(updates)
      .catch(function (err) {
        console.error(err);
      })
      .then(function () {
        window.location.href = '/checkout';
      });
  }

  // Checkout interception
  function runCheckoutIntercept(event, onProceed) {
    if (!CHECKOUT_INTERCEPT_ENABLED) {
      onProceed();
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    classifyCart()
      .then(function (pickupItems) {
        if (!pickupItems) {
          onProceed();
          return;
        }
        pendingPickupItems = pickupItems;
        showWarning(pickupItems);
      })
      .catch(function (err) {
        onProceed(); // never block the customer
      });
  }

  // Form submit (type="submit" checkout buttons on /cart page)
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.querySelector('[name="checkout"]')) return;
      if (form._cartNormalizerBusy) return;
      form._cartNormalizerBusy = true;

      var checkoutBtn = form.querySelector('[name="checkout"]');
      if (checkoutBtn) checkoutBtn.disabled = true;

      runCheckoutIntercept(event, function () {
        if (checkoutBtn) checkoutBtn.disabled = false;
        form._cartNormalizerBusy = false;
        form.submit();
      });
    },
    true
  );

  // Click intercept for type="button" checkout buttons (Kaching drawer button)
  document.addEventListener(
    'click',
    function (event) {
      var btn = event.target.closest('button[name="checkout"]');
      if (!btn) return;
      if (btn.type === 'submit') return;
      if (btn._cartNormalizerBusy) return;
      btn._cartNormalizerBusy = true;
      btn.disabled = true;

      runCheckoutIntercept(event, function () {
        btn.disabled = false;
        btn._cartNormalizerBusy = false;
        window.location.href = '/checkout';
      });
    },
    true
  );

  // Notice button clicks
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-cart-normalizer-confirm]')) {
      event.preventDefault();
      handleConfirm();
      return;
    }
    if (event.target.closest('[data-cart-normalizer-cancel]')) {
      event.preventDefault();
      pendingPickupItems = null;
      hideNotice();

      // Re-enable any disabled checkout buttons
      document.querySelectorAll('button[name="checkout"], [data-kaching-checkout]').forEach(function (btn) {
        btn.disabled = false;
        btn._cartNormalizerBusy = false;
      });
    }
  });

  // ---------------------------------------------------------------------------
  // Add-to-cart intercept — pickup-only confirmation
  // ---------------------------------------------------------------------------

  // Cache variant classification results to avoid repeated API calls
  var pickupOnlyCache = {};

  var skipPickupConfirmHandles = config.skipPickupConfirmHandles || [];

  // IO-4944 — off on markets that don't offer store pickup (LV has no KIKA stores).
  // Defaults to enabled so an older cached config keeps the LT behaviour.
  var pickupConfirmEnabled = config.pickupConfirmEnabled !== false;

  function shouldSkipPickupConfirm(form) {
    if (!pickupConfirmEnabled) return true;
    if (!skipPickupConfirmHandles.length) return false;

    var productInfo = form && form.closest('product-info');
    if (productInfo) {
      var url = productInfo.getAttribute('data-url') || '';
      var handle = url.replace(/^\/products\//, '').split('?')[0];
      if (skipPickupConfirmHandles.indexOf(handle) !== -1) return true;
    }

    var pathMatch = window.location.pathname.match(/\/products\/([^/]+)/);
    return pathMatch && skipPickupConfirmHandles.indexOf(pathMatch[1]) !== -1;
  }

  function getConfirmEl() { return document.getElementById('cart-normalizer-pickup-confirm'); }

  function showPickupConfirm(submitBtn) {
    var el = getConfirmEl();
    if (!el) {
      console.error(TAG, '#cart-normalizer-pickup-confirm element not found in DOM — re-enabling button and proceeding.');
      if (submitBtn) submitBtn.disabled = false;
      return false;
    }
    el.hidden = false;
    console.info(TAG, 'Pickup confirm popup shown. el.hidden =', el.hidden);
    return true;
  }

  function hidePickupConfirm() {
    var el = getConfirmEl();
    if (el) el.hidden = true;
  }

  function isVariantPickupOnly(variantId) {
    if (pickupOnlyCache[variantId] !== undefined) {
      return Promise.resolve(pickupOnlyCache[variantId]);
    }
    var gid = 'gid://shopify/ProductVariant/' + variantId;
    return fetchVariantData([gid]).then(function (variantMap) {
      var data         = variantMap[gid];
      var nodes        = (data && data.storeAvailability && data.storeAvailability.nodes) || [];
      var totalQty     = (data && typeof data.quantityAvailable === 'number') ? data.quantityAvailable : null;
      var pickupQty    = nodes.reduce(function (s, n) { return s + (n.quantityAvailable || 0); }, 0);
      var warehouseQty = (totalQty !== null) ? (totalQty - pickupQty) : 1;
      var result       = warehouseQty <= 0;
      pickupOnlyCache[variantId] = result;
      console.info(TAG, 'Add-to-cart check: variant', variantId, '→', result ? 'PICKUP-ONLY' : 'SHIPPABLE');
      return result;
    });
  }

  // Intercept add-to-cart form submits (capture phase runs before product-form custom element)
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || form.getAttribute('data-type') !== 'add-to-cart-form') return;

      // Already confirmed by the user — let it through
      if (form._pickupConfirmed) {
        form._pickupConfirmed = false;
        return;
      }

      // Digital / special products — no pickup-only warning on add to cart
      if (shouldSkipPickupConfirm(form)) {
        return;
      }

      // Already waiting for an API response — swallow the duplicate
      if (form._pickupChecking) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      var variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;
      var variantId = variantInput.value;

      event.preventDefault();
      event.stopImmediatePropagation();

      form._pickupChecking = true;
      var submitBtn = form.querySelector('[type="submit"][name="add"]');
      if (submitBtn) submitBtn.disabled = true;

      isVariantPickupOnly(variantId)
        .then(function (isPickupOnly) {
          form._pickupChecking = false;

          if (!isPickupOnly) {
            if (submitBtn) submitBtn.disabled = false;
            form._pickupConfirmed = true;
            form.requestSubmit ? form.requestSubmit(submitBtn) : form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            return;
          }

          // Pickup-only: show confirmation popup, save pending state
          var shown = showPickupConfirm(submitBtn);
          if (!shown) {
            // Fallback: element missing, just add to cart
            form._pickupConfirmed = true;
            form.requestSubmit ? form.requestSubmit(submitBtn) : form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
            return;
          }
          window._pendingPickupForm = form;
          window._pendingPickupBtn  = submitBtn;
        })
        .catch(function (err) {
          console.error(TAG, 'Variant check failed, proceeding to add cart:', err);
          form._pickupChecking = false;
          if (submitBtn) submitBtn.disabled = false;
          form._pickupConfirmed = true;
          form.requestSubmit ? form.requestSubmit(submitBtn) : form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
        });
    },
    true
  );

  // Pickup confirm/cancel button clicks
  document.addEventListener('click', function (event) {
    if (event.target.closest('[data-pickup-confirm]')) {
      event.preventDefault();
      hidePickupConfirm();

      // Callback path (used by search-page AJAX ATC)
      var cb = window._pendingPickupCallback;
      if (cb) {
        window._pendingPickupCallback = null;
        if (cb.confirm) cb.confirm();
        return;
      }

      // Form-submit path (used by collection/product-page ATC)
      var form      = window._pendingPickupForm;
      var submitBtn = window._pendingPickupBtn;
      window._pendingPickupForm = null;
      window._pendingPickupBtn  = null;
      if (!form) return;
      if (submitBtn) submitBtn.disabled = false;
      form._pickupConfirmed = true;
      form.requestSubmit ? form.requestSubmit(submitBtn) : form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }));
      return;
    }

    if (event.target.closest('[data-pickup-cancel]')) {
      event.preventDefault();
      hidePickupConfirm();

      // Callback path
      var cb = window._pendingPickupCallback;
      if (cb) {
        window._pendingPickupCallback = null;
        if (cb.cancel) cb.cancel();
        return;
      }

      // Form-submit path
      var submitBtn    = window._pendingPickupBtn;
      var pendingForm  = window._pendingPickupForm;
      window._pendingPickupForm = null;
      window._pendingPickupBtn  = null;
      if (submitBtn) submitBtn.disabled = false;
      // Re-enable search-page ATC button stored on the hidden form
      if (pendingForm && pendingForm._snizeAtcBtn) {
        pendingForm._snizeAtcBtn.disabled = false;
        pendingForm._snizeAtcBtn.classList.remove('snize-kika-atc--loading');
        if (pendingForm.parentNode) pendingForm.parentNode.removeChild(pendingForm);
      }
    }
  });

  // Public API
  window.CartNormalizer = {
    check: classifyCart,

    // Used by AJAX add-to-cart flows (e.g. search-page cards) that don't use
    // form submission. Checks pickup status, shows the popup if needed, and
    // calls onConfirm or onCancel when the user responds.
    requestAddToCart: function (variantId, onConfirm, onCancel) {
      // IO-4944 — no pickup prompt on markets without store pickup; add straight to cart.
      if (!pickupConfirmEnabled) {
        onConfirm();
        return;
      }
      isVariantPickupOnly(variantId)
        .then(function (isPickupOnly) {
          if (!isPickupOnly) {
            onConfirm();
            return;
          }
          var shown = showPickupConfirm(null);
          if (!shown) {
            onConfirm(); // popup missing — proceed anyway
            return;
          }
          window._pendingPickupCallback = { confirm: onConfirm, cancel: onCancel };
          window._pendingPickupForm = null;
          window._pendingPickupBtn  = null;
        })
        .catch(function () {
          onConfirm(); // on API error, don't block the user
        });
    },
  };
})();
